import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import Box from '@material-ui/core/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { ListKits } from '@src/types/graphql-types'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import InfoIcon from '@material-ui/icons/Info'
import { Grid, MenuItem, Typography } from '@material-ui/core'
import LiquidSelect from '@src/components/LiquidSelect'
import { SelectedDataType } from './DesignAndPriceModal'
import AnalyteInfoMessage from '../AnalyteInfoMessage'

const useStyles = makeStyles((theme: Theme) => {
  const disabledCellBorder = `0.5px solid ${theme.palette.grey[800]}`
  return {
    modalBodyContainerStyle: {
      margin: theme.spacing(6, 8, 3, 8),
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(2, 8, 2, 8),
      },
    },

    disabledCellContainer: {
      padding: theme.spacing(3),
      borderRight: disabledCellBorder,
      borderBottom: disabledCellBorder,
      '&:nth-child(3n+1)': {
        borderLeft: disabledCellBorder,
      },
      '&:nth-child(-n+3)': {
        borderTop: disabledCellBorder,
      },
      backgroundColor: 'rgba(201, 201, 201, 0.25)',
    },

    checkboxRoot: {
      paddingBottom: theme.spacing(1),
      alignSelf: 'flex-start',
    },
    tableWrapper: {
      marginTop: theme.spacing(2),
    },

    buttonStyle: {
      color: theme.palette.primary.dark,
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightBold,
    },
    headerText: {
      fontWeight: theme.typography.fontWeightBold,
    },
    brandPrimary: {
      backgroundColor: theme.palette.secondary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: '#402874',
      },
    },
    premixedAnalyteInfo: {
      margin: theme.spacing(4, 0, 4, 0),
      fontSize: theme.typography.pxToRem(14),
    },
    infoIcon: {
      transform: 'scale(1.2)',
      color: theme.palette.primary.main,
      height: theme.spacing(3.5),
      verticalAlign: 'bottom',
      cursor: 'pointer',
    },
    premixedInfoContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    sampleTypeSelect: {
      width: 205,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(1),
    },
    sampleTypeHeader: {
      paddingTop: theme.spacing(5),
      fontWeight: theme.typography.fontWeightBold,
    },
  }
})

interface PremixedProductProps {
  milliplexKit: ListKits | null
  sampleType: string
  sampleTypeList: string[]
  handleSampleTypeSelection: (sampleType: string) => void
  fetchKitDetails: (params: SelectedDataType) => void
  enabledAnalytesList: string[]
}

const PremixedProduct: React.FC<PremixedProductProps> = ({
  milliplexKit,
  sampleType,
  sampleTypeList,
  handleSampleTypeSelection,
  fetchKitDetails,
  enabledAnalytesList,
}) => {
  const classes = useStyles()
  const renderedValue = (value) => {
    if (value === '') return 'Select'
    return value
  }

  useEffect(() => {
    milliplexKit?.requireSample &&
      fetchKitDetails({ analytes: enabledAnalytesList })
  }, [])

  return (
    <Box>
      <Box className={classes.premixedInfoContainer}>
        <InfoIcon className={classes.infoIcon} />
        <Typography className={classes.premixedAnalyteInfo}>
          <FormattedMessage
            id="MILLIPLEX_PREMIXED_ANALYTES"
            defaultMessage="Premixed products cannot be customized to plex with other analytes."
          />
        </Typography>
      </Box>

      <Grid container className={classes.tableWrapper}>
        {milliplexKit?.analytes
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
          .map((analyte) => {
            return (
              <Grid
                item
                xs={4}
                key={analyte}
                className={classes.disabledCellContainer}
              >
                <LiquidCheckbox
                  aria-label={analyte}
                  value={analyte}
                  checked
                  disabled
                  classes={{
                    root: classes.checkboxRoot,
                  }}
                  label={analyte}
                />
              </Grid>
            )
          })}
      </Grid>
      <AnalyteInfoMessage analytes={milliplexKit?.analytes || []} />
      {milliplexKit?.requireSample &&
        sampleTypeList &&
        sampleTypeList.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography className={classes.sampleTypeHeader}>
                <FormattedMessage
                  id="CONF_SAMPLE_TYPE"
                  defaultMessage="Sample Type"
                />
              </Typography>
            </Grid>{' '}
            <Grid item xs={12}>
              <LiquidSelect
                filled
                onChange={(e) =>
                  handleSampleTypeSelection(e.target.value as string)
                }
                value={sampleType}
                className={classes.sampleTypeSelect}
                renderValue={renderedValue}
                displayEmpty
              >
                {['Select'].concat(sampleTypeList).map((value) => (
                  <MenuItem key={value} value={value}>
                    <FormattedMessage
                      id={`MILLIPLEX_${value}`}
                      defaultMessage={value}
                    />
                  </MenuItem>
                ))}
              </LiquidSelect>
            </Grid>
          </>
        )}
    </Box>
  )
}
export default PremixedProduct
